import React from 'react';
import { Hammer, Sprout, Home as HomeIcon, Leaf, Sun, Waves, Coffee, BookOpen } from 'lucide-react';
import { useReveal, JoinBlock, InstagramSection, PageHeader } from '../components/shared';

const CHAPTERS = [
  [Hammer, 'The bones', 'A tired weatherboard on a windswept block behind the dunes. We kept what was honest — the floorboards, the proportions, the north light — and stripped back the rest.'],
  [Sprout, 'The garden first', 'Before the kitchen was finished, the dahlia beds were in. We figured if the paddock was thriving, the house would catch up. It mostly has.'],
  [HomeIcon, 'Living in it', 'Still unfinished, always evolving. The farmhouse is a home first, a workroom second, and every so often a place to sit thirty people down to lunch among the flowers.'],
];

const PRODUCTS = [
  ['Walls', 'Limewash paint', 'Chalky, breathable and forgiving — it hides the bumps in old plaster and takes the coastal light beautifully. Every wall in the house.'],
  ['Floors', 'Sanded original boards', 'Brought back with a water-based matte finish rather than replaced. Sandy feet welcome.'],
  ['Kitchen', 'Timber and stone', 'A long recycled-hardwood bench, a deep farmhouse sink for buckets of stems, and hooks for everything.'],
  ['Linen', 'Australian-made linen', 'On the beds, over the table, wrapped around bunches. It gets softer every season and never needs ironing.'],
  ['Paddock', 'Felco secateurs & a hori hori', 'The two tools we actually reach for every morning — one for cutting, one for planting, weeding and dividing tubers.'],
  ['Water', 'Rainwater tanks & drip lines', 'Sandy soil drinks fast. Catching every drop off the roof and running drip lines under mulch keeps the dahlias — and the water bill — happy.'],
];

const IDEAS = [
  ['Our first-year dahlia patch', 'We planted the dahlias before the house was finished — a small trial patch that taught us more about our sandy soil and salt wind than any book could.'],
  ['Design for the mess', 'A mudroom for boots and buckets means the rest of the house stays a home.'],
  ['Follow the light, not the plan', 'We moved the dining table three times before it landed where the afternoon sun does.'],
  ['Buy once, buy well', 'Fewer, better things — tools, linen, chairs — that get more beautiful with use.'],
];

const RITUALS = [
  [Waves, 'Surf before the paddock'],
  [Coffee, 'Coffee on the back step'],
  [Sun, 'Cut flowers in the cool of the morning'],
  [Leaf, 'Dinner outside, among the dahlias'],
  [BookOpen, 'Sunday: market, then nothing'],
];

export default function Farmhouse({ go }) {
  useReveal([]);
  return (
    <div className="page active" data-testid="farmhouse-page">
      <PageHeader eyebrow="Building a Home & a Flower Farm" title="The Farmhouse" sub="Our journey, the things we used, and a slower way of living" cta="Join Us for the Journey" ctaPink onCta={() => go('tubers', { scrollToJoin: true })} />

      <div className="fh-intro">
        <span className="eyebrow rev">Torquay Flower Farm, opened up</span>
        <h2 className="serif-h rev">A working flower farm,<em>with the door left open.</em></h2>
        <p className="body-text rev">The Farmhouse is the slower side of South Beach Blooms — the story of building a home and a flower farm at the same time, on a windswept block behind the dunes. Here we share what we&rsquo;re making, the products and makers we&rsquo;ve leaned on, the ideas that stuck (and the ones that didn&rsquo;t), and the small rituals that make a slower life on the Surf Coast feel like ours.</p>
      </div>

      <div className="offer-grid fh-offers" data-testid="farmhouse-chapters">
        {CHAPTERS.map(([Ic, t, d], i) => (
          <div className="offer-card rev" key={i}>
            <span className="offer-icon"><Ic size={30} strokeWidth={1.3} /></span>
            <div className="offer-title">{t}</div>
            <p className="offer-body">{d}</p>
          </div>
        ))}
      </div>

      <div className="split home-split">
        <div className="split-photo bg-photo rev" style={{ backgroundImage: 'url(/brand/pages/farmhouse-build.webp)' }} />
        <div className="split-copy">
          <span className="eyebrow rev">The Build</span>
          <h2 className="home-split-h rev">Two projects,<em>one paddock</em></h2>
          <p className="body-text rev">We didn&rsquo;t plan to renovate a house and start a flower farm in the same year. But the block came with both — a weatherboard that needed love and a paddock that needed purpose — so we said yes to the lot. Most weekends still look like a paintbrush in one hand and a trowel in the other.</p>
          <p className="body-text rev">We&rsquo;re documenting it as we go — the wins, the mistakes, and the products that earned their place — for anyone dreaming of doing the same.</p>
        </div>
      </div>

      <div className="fh-products" data-testid="farmhouse-products">
        <span className="eyebrow rev" style={{ textAlign: 'center', display: 'block' }}>Things we used &amp; loved</span>
        <h2 className="serif-h rev" style={{ textAlign: 'center' }}>Tried, tested,<em> and still in use.</em></h2>
        <div className="fh-products-grid">
          {PRODUCTS.map(([cat, t, d], i) => (
            <div className="fh-product rev" key={i} data-testid={`farmhouse-product-${i}`}>
              <span className="fh-product-cat">{cat}</span>
              <div className="fh-product-t">{t}</div>
              <p className="fh-product-d">{d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="split home-split">
        <div className="split-copy" style={{ order: 1 }}>
          <span className="eyebrow rev">Ideas that stuck</span>
          <h2 className="home-split-h rev">Notes from<em>the build</em></h2>
          <ul className="fh-ideas rev">
            {IDEAS.map(([t, d], i) => (
              <li key={i}><strong>{t}</strong><span>{d}</span></li>
            ))}
          </ul>
        </div>
        <div className="split-photo bg-photo rev" style={{ order: 2, backgroundImage: 'url(/brand/pages/farmhouse-shoot.webp)' }} />
      </div>

      <div className="fh-slow" data-testid="farmhouse-slow">
        <div className="fh-slow-photo bg-photo rev" style={{ backgroundImage: 'url(/brand/pages/farmhouse-porch.webp)' }} />
        <div className="fh-slow-copy">
          <span className="eyebrow rev">A vision for a slower life</span>
          <h2 className="serif-h rev">Less rush,<em> more mornings.</em></h2>
          <p className="body-text rev">We moved here for the water and stayed for the pace. The farm sets the rhythm now — seasons, not deadlines — and the farmhouse is where we slow down enough to notice it. This is what a good day looks like for us.</p>
          <ul className="fh-rituals rev">
            {RITUALS.map(([Ic, t], i) => (
              <li key={i}><Ic size={16} strokeWidth={1.5} /><span>{t}</span></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="fh-open" data-testid="farmhouse-open-door">
        <span className="eyebrow rev">The door is open</span>
        <h2 className="serif-h rev">Photographers, makers and brands<em> who feel at home here &mdash; say hello.</em></h2>
        <p className="body-text rev">Everything is grown here and styled by us, so what you shoot or sit down to is genuinely of this place. We take on a small number of shoots, gatherings and collaborations each season.</p>
        <div className="rev"><button className="btn btn-outline" onClick={() => go('contact')} data-testid="farmhouse-contact-btn">Get in Touch</button></div>
      </div>

      <JoinBlock eyebrow="Follow the Build" headline="Join the community.<br>The farmhouse story, as it happens." body="Renovation updates, the products we swear by, slow-living notes and first dibs on dahlia tubers — to our community list before Instagram." />

      <InstagramSection />
    </div>
  );
}
